<template>
  <modal-dialog
    :visible="dialog"
    persistent
    scrollable
    container-class="pa-0 pt-2"
    :title="$t('label.contact_details')"
  >
    <validation-observer ref="observer">
      <v-row>
        <v-col
          cols="12"
          class="pb-0"
        >
          <validation-provider
            v-slot="{ errors }"
            rules="max:999"
          >
            <v-textarea
              v-model="c.notes"
              :label="$t('label.notes')"
              rows="2"
              :error-messages="errors"
              :hide-details="!errors || errors.length === 0"
            />
          </validation-provider>
        </v-col>
        <v-col
          cols="6"
          class="py-0"
        >
          <v-switch
            v-model="c.isVIP"
            prepend-icon="mdi-crown"
            :label="$t('label.vip')"
          />
        </v-col>

        <row-form-divider />

        <v-col
          class="pb-0"
          md="2"
          cols="4"
        >
          <v-select
            v-model="c.title"
            :items="contactTitles"
            :label="$t('label.person_title')"
          />
        </v-col>
        <v-col
          class="pb-0"
          md="10"
          cols="8"
        >
          <validation-provider
            v-slot="{errors}"
            rules="max:49"
          >
            <v-text-field
              v-model="c.name"
              :error-messages="errors"
              :label="$t('label.person_name')"
              persistent-hint
              :hint="totalVisitsText"
              autocomplete="off"
              aria-autocomplete="off"
              name="notASearchField"
              role="presentation"
            />
          </validation-provider>
        </v-col>

        <v-col
          md="6"
          cols="12"
          class="py-0"
        >
          <validation-provider
            v-slot="{errors}"
            rules="max:254"
          >
            <v-text-field
              v-model="c.companyName"
              :error-messages="errors"
              :label="$t('label.company')"
              autocomplete="off"
              aria-autocomplete="off"
              name="notASearchField"
              role="presentation"
            />
          </validation-provider>
        </v-col>

        <v-col
          class="py-0"
          md="6"
          cols="12"
        >
          <validation-provider
            v-slot="{errors}"
            :rules="`max:19|validPhone:${defaultCC}`"
          >
            <v-text-field
              v-model="c.phone"
              :error-messages="errors"
              :label="$t('label.phone_number')"
              autocomplete="off"
              aria-autocomplete="off"
              name="notASearchField"
              role="presentation"
            />
          </validation-provider>
        </v-col>

        <v-col
          md="6"
          cols="12"
          class="py-0"
        >
          <validation-provider
            v-slot="{errors}"
            rules="max:254|validEmail"
          >
            <v-text-field
              v-model="c.email"
              :label="$t('label.email')"
              :error-messages="errors"
              @input="changedEmail"
            />
          </validation-provider>
        </v-col>

        <v-col
          cols="12"
          md="6"
          class="py-0"
        >
          <v-switch
            v-model="c.newsletterSubscription"
            :label="$t('label.newsletter_subscription')"
          />
        </v-col>

        <v-col
          cols="8"
          class="py-0"
        >
          <validation-provider
            v-slot="{errors}"
            rules="max:99"
          >
            <v-text-field
              v-model="c.address"
              :error-messages="errors"
              :label="$t('label.street_address')"
              autocomplete="off"
              aria-autocomplete="off"
              name="notASearchField"
              role="presentation"
            />
          </validation-provider>
        </v-col>

        <v-col
          cols="4"
          class="py-0"
        >
          <validation-provider
            v-slot="{errors}"
            rules="max:19"
          >
            <v-text-field
              v-model="c.postalCode"
              :error-messages="errors"
              :label="$t('label.zip_code')"
              autocomplete="off"
              aria-autocomplete="off"
              name="notASearchField"
              role="presentation"
            />
          </validation-provider>
        </v-col>

        <v-col
          cols="6"
          class="py-0"
        >
          <validation-provider
            v-slot="{errors}"
            rules="max:49"
          >
            <v-text-field
              v-model="c.city"
              :error-messages="errors"
              :label="$t('label.city_and_state')"
              autocomplete="off"
              aria-autocomplete="off"
              name="notASearchField"
              role="presentation"
            />
          </validation-provider>
        </v-col>

        <v-col
          cols="6"
          class="py-0"
        >
          <v-select
            v-model="c.country"
            return-object
            :items="countries"
            :label="$t('label.country')"
          />
        </v-col>

        <row-form-divider />

        <v-col
          cols="6"
          class="pb-0"
        >
          <v-select
            v-model="c.preferredLanguage"
            :items="languages"
            :label="$t('label.language')"
          />
        </v-col>

        <v-col
          cols="6"
          class="pb-0"
        >
          <date-picker-field
            :label="$t('label.birthday')"
            :value="c.dateOfBirth"
            clearable
            type="date"
            :format-fn="formatBirthday"
            @input="dateOfBirthChange"
          />
        </v-col>
      </v-row>
    </validation-observer>
    <template #actions>
      <v-spacer />
      <v-btn
        color="success"
        :disabled="!isReservationsAllowed || !isChanged"
        @click="save"
      >
        {{ $t('button.save') }}
      </v-btn>
      <v-btn
        color="error"
        text
        @click="close"
      >
        {{ $t('button.close') }}
      </v-btn>
    </template>
    <confirmation-dialog
      v-if="confirmationDialogVisible"
      :visible="confirmationDialogVisible"
      :message="$t('message.confirm_discard_changes')"
      @confirm="cancel"
      @close="confirmationDialogVisible=false"
    />
  </modal-dialog>
</template>

<script lang="ts">
import Vue, { nextTick, PropType } from 'vue';
import { ValidationObserver } from 'vee-validate';
import DialogClosable from '@/mixins/dialog-closable';
import Contact from '@/model/Contact';
import { cloneModel, isModelEqualToModel } from '@/model/model-utils';
import Country from '@/model/Country';
import Reservation from '@/model/Reservation';
import { contactTitleItems } from '@/util/codes';
import UnsavedGuard from '@/mixins/unsaved-guard';
import { removeInvisibleUnicodeCharacters } from '@/util/common';

export default Vue.extend({
  name: 'ContactDetailsDialog',
  mixins: [DialogClosable, UnsavedGuard],
  props: {
    contact: { type: Object as PropType<Contact|null>, required: false, default: null },
  },
  data() {
    return {
      contactTitles: contactTitleItems(),
      newc: new Contact(), // direct editing: new contact duplicated from prop if any
      oldc: new Contact(), // reservation editing: old contact from reservation
      confirmationDialogVisible: false,
    };
  },
  computed: {
    isReservationEditing(): boolean {
      return this.contact === null;
    },
    r(): Reservation {
      return this.$tstore.getters.editReservation ?? new Reservation();
    },
    c(): Contact {
      if (!this.isReservationEditing) return this.newc; // direct contact editing
      return this.r.contact ?? new Contact(); // reservation editing
    },
    countries(): Country[] {
      return this.$tstore.getters.sortedCountries;
    },
    languages(): String[] {
      return this.$tstore.getters.sortedLocales;
    },
    totalVisitsText(): string {
      return this.c.reservationCount && this.c.reservationCount > 0
        ? this.$i18n.t('label.total_visits_x', [this.c.reservationCount]) as string
        : '';
    },
    // eslint-disable-next-line vue/no-unused-properties
    isChanged(): boolean {
      return !isModelEqualToModel(this.c, this.oldc);
    },
    isReservationsAllowed(): boolean {
      return this.$tstore.getters.isReservationsAllowed;
    },
    defaultCC(): string {
      const { account } = this.$tstore.state.settings;
      return this.newc.country?.code || account.countryCode || '';
    },
  },
  watch: {
    visible(value: boolean) {
      if (value) {
        if (!this.isReservationEditing) { // direct contact editing
          this.newc = cloneModel(this.contact!);
          this.oldc = cloneModel(this.contact!);
        } else this.oldc = cloneModel(this.r.contact); // reservation editing
      }
    },
  },
  created() {
    this.oldc = cloneModel(this.r.contact);
  },
  mounted() {
    console.log('Contact Details mounted: ', this.contact);
    if (!this.isReservationEditing) { // direct contact editing
      this.newc = cloneModel(this.contact!);
      this.oldc = cloneModel(this.contact!);
    } else this.oldc = cloneModel(this.r.contact); // reservation editing
  },
  methods: {
    changedEmail() {
      this.$nextTick(() => {
        this.c.email = removeInvisibleUnicodeCharacters(this.c.email || '').trim();
      });
    },
    formatBirthday(d: Date): string | null {
      return this.$localized.dateText(d);
    },
    dateOfBirthChange(date: Date) {
      this.c.dateOfBirth = date ?? undefined;
    },
    async save() {
      const isValid = await (this.$refs.observer as InstanceType<typeof ValidationObserver>).validate();
      if (!isValid) { return; }

      if (!this.isReservationEditing) this.$emit('save', this.newc); // direct contact editing
      else this.$emit('close', false); // reservation editing
    },
    close() {
      // TODO alert if changed, discard changes
      if (this.isChanged) {
        this.confirmationDialogVisible = true;
      } else {
        this.cancel();
      }
    },
    cancel() {
      if (this.isReservationEditing) this.r.contact = this.oldc; // reservation editing
      this.$emit('close', false);
    },
  },
});
</script>
